<p-toast></p-toast>
<div *ngIf="isLoading" class="flex justify-content-center align-items-center h-full">
    <p-progressSpinner></p-progressSpinner>
</div>

<div *ngIf="!isLoading && selloutHeader" class="surface-ground h-full overflow-y-auto p-4">
    <div class="flex justify-content-between align-items-start mb-4">
        <div>
            <h1 class="text-blue-800 text-3xl font-bold">Sellout #{{ selloutHeader.selloutId }}</h1>
            <div class="text-700 mt-2">{{ selloutHeader.customer.customerNumber }} - {{ selloutHeader.customer.customerName }}</div>
        </div>
        <div>
            <p-tag [value]="selloutHeader.workflowStatus?.statusName?.toUpperCase()" [severity]="getStatusSeverity()"
                [rounded]="true" class="mr-2"></p-tag>
            <p-tag [value]="(selloutHeader.validationStatus || 'UNKNOWN').toUpperCase()"
                [severity]="getValidationTagSeverity(selloutHeader.validationStatus)" [rounded]="true"
                class="mr-2"></p-tag>
            <p-button *ngIf="canShowHeaderOverrideAction" label="Variance Override" icon="pi pi-pencil"
                severity="warn" class="mr-2" (onClick)="openOverrideDialog()"></p-button>
            <p-button label="Generate Settlement" icon="pi pi-plus" severity="success"
                *ngIf="selloutHeader.workflowStatus?.statusName === 'Not Settled'" (onClick)="generateNewSettlement()"
                class="mr-2" [disabled]="!canGenerateSettlement"></p-button>
            <p-button *ngIf="selloutHeader.linkedSessionId" label="Open Session" icon="pi pi-telegram" class="mr-2"
                severity="info" (onClick)="openSession()"></p-button>
            <p-button icon="pi pi-file-excel" label="Export to Excel" styleClass="p-button-help"
                (onClick)="exportDetailsToExcel()"></p-button>
        </div>
    </div>

    <div class="card p-4 mb-4 surface-card border-round-md shadow-2">
        <h3 class="text-xl font-semibold mb-3 text-900 border-bottom-1 surface-border pb-2">Sellout Header Overview</h3>
        
        <div class="grid">
            <!-- Group 1: General & Customer Information -->
            <div class="col-12 lg:col-6 mb-3">
                <div class="p-3 border-round surface-50 h-full border-1 surface-border">
                    <div class="font-bold text-lg text-blue-800 mb-3 flex align-items-center gap-2">
                        <i class="pi pi-id-card"></i> General & Customer Information
                    </div>
                    <div class="grid">
                        <div class="col-12 md:col-6"><strong>Sellout ID:</strong> {{ selloutHeader.selloutId }}</div>
                        <div class="col-12 md:col-6"><strong>Settlement ID:</strong> {{ selloutHeader.settlementId || '-' }}</div>
                        <div class="col-12 md:col-6"><strong>Customer:</strong> {{ selloutHeader.customer.customerNumber ? selloutHeader.customer.customerNumber + ' - ' : '' }}{{ selloutHeader.customer.customerName }}</div>
                        <div class="col-12 md:col-6"><strong>Claim Brand:</strong> {{ selloutHeader.business?.businessName || '-' }}</div>
                        <div class="col-12 md:col-6"><strong>Channel:</strong> {{ selloutHeader.channel || '-' }}</div>
                        <div class="col-12 md:col-6"><strong>City:</strong> {{ selloutHeader.city || '-' }}</div>
                        <div class="col-12 md:col-6"><strong>Execution Type:</strong> {{ selloutHeader.executionType || '-' }}</div>
                        <div class="col-12 md:col-6"><strong>Batch #:</strong> {{ selloutHeader.batchNumber || '-' }}</div>
                        <div class="col-12 md:col-6"><strong>Uploaded By:</strong> {{ selloutHeader.createdBy || '-' }}</div>
                        <div class="col-12 md:col-6"><strong>Uploaded At:</strong> {{ selloutHeader.createdAt | appDate:'medium' }}</div>
                    </div>
                </div>
            </div>

            <!-- Group 2: Execution & Plan Window -->
            <div class="col-12 lg:col-6 mb-3">
                <div class="p-3 border-round surface-50 h-full border-1 surface-border">
                    <div class="font-bold text-lg text-blue-800 mb-3 flex align-items-center gap-2">
                        <i class="pi pi-calendar"></i> Execution & Plan Window
                    </div>
                    <div class="grid">
                        <div class="col-12 md:col-6"><strong>Sellout Start Date:</strong> {{ selloutHeader.selloutStartDate | date }}</div>
                        <div class="col-12 md:col-6"><strong>Sellout End Date:</strong> {{ selloutHeader.selloutEndDate | date }}</div>
                        <div class="col-12"><strong>Promo Plans:</strong> {{ promoPlanIdsLabel }}</div>
                    </div>
                </div>
            </div>

            <!-- Group 3: Financial Summary & Settlement Comparison -->
            <div class="col-12 lg:col-6 mb-3">
                <div class="p-3 border-round surface-50 h-full border-1 surface-border">
                    <div class="font-bold text-lg text-blue-800 mb-3 flex align-items-center gap-2">
                        <i class="pi pi-money-bill"></i> Financial Summary & Settlement Comparison
                    </div>
                    <div class="grid">
                        <div class="col-12 md:col-6"><strong>Detail Count:</strong> {{ detailRows.length }} line(s)</div>
                        <div class="col-12 md:col-6"><strong>Total Quantity:</strong> {{ totalQuantity | number }}</div>
                        <div class="col-12 md:col-6"><strong>Total Sellout Amount:</strong> {{ totalAmount | currency:'SAR':'symbol':'1.2-2' }}</div>
                        <div class="col-12 md:col-6">
                            <strong>Settlement Amount:</strong> 
                            <span *ngIf="settlementAmount != null">{{ settlementAmount | currency:'SAR':'symbol':'1.2-2' }}</span>
                            <span *ngIf="settlementAmount == null">-</span>
                        </div>
                        <div class="col-12" *ngIf="settlementAmount != null">
                            <strong>Amount Match Status:</strong>
                            <p-tag [value]="isAmountWithinVariance ? 'WITHIN TOLERANCE (±5%)' : 'AMOUNT OUTSIDE ±5% TOLERANCE'"
                                [severity]="isAmountWithinVariance ? 'success' : 'danger'" styleClass="ml-2"></p-tag>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Group 4: Validation & Override Details -->
            <div class="col-12 lg:col-6 mb-3">
                <div class="p-3 border-round surface-50 h-full border-1 surface-border">
                    <div class="font-bold text-lg text-blue-800 mb-3 flex align-items-center gap-2">
                        <i class="pi pi-shield"></i> Validation & Override Details
                    </div>
                    <div class="grid">
                        <div class="col-12 md:col-6">
                            <strong>Validation Status:</strong>
                            <p-tag [value]="selloutHeader.validationStatus || 'UNKNOWN'"
                                [severity]="getValidationTagSeverity(selloutHeader.validationStatus)" styleClass="ml-2"></p-tag>
                        </div>
                        <div class="col-12 md:col-6"><strong>Validated / Overridden By:</strong> {{ selloutHeader.overrideBy || selloutHeader.createdBy || '-' }}</div>
                        <div class="col-12 md:col-6"><strong>Override Status:</strong> {{ selloutHeader.overrideStatus || '-' }}</div>
                        <div class="col-12 md:col-6"><strong>Override At:</strong> {{ selloutHeader.overrideAt | appDate:'medium' }}</div>
                        <div class="col-12 md:col-6"><strong>Override Eligible:</strong> {{ isOverrideEligible ? 'Yes' : 'No' }}</div>
                        <div class="col-12"><strong>Override Justification:</strong> {{ selloutHeader.overrideJustification || '-' }}</div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div class="mt-4">
        <p-fieldset legend="Sellout Details" [toggleable]="true" class="mb-4">
            <p-table [value]="detailRows" responsiveLayout="scroll" styleClass="p-datatable-sm sellout-details-table">
                <ng-template pTemplate="header">
                    <tr>
                        <th>Promo Plan</th>
                        <th>Item Mapping</th>
                        <th>Date Window</th>
                        <th>Commercials</th>
                        <th>Quantity Check</th>
                        <th>Validation</th>
                    </tr>
                </ng-template>
                <ng-template pTemplate="body" let-detail>
                    <tr>
                        <td class="compact-cell">
                            <div class="stacked-cell">
                                <div><span class="cell-label">PP ID:</span> {{ detail.matchedPromoPlanId ?? '-' }}</div>
                                <div><span class="cell-label">Line:</span> {{ detail.matchedPromoPlanLineId ?? '-' }}</div>
                                <div><span class="cell-label">Batch:</span> {{ detail.promoPlanBatchNumber || '-' }}</div>
                            </div>
                        </td>
                        <td class="compact-cell">
                            <div class="stacked-cell">
                                <div><span class="cell-label">Customer:</span> {{ getCustomerItemLabel(detail) }}</div>
                                <div><span class="cell-label">Uploaded:</span> {{ detail.uploadedCustomerItemNumber || '-' }}</div>
                                <div><span class="cell-label">Matched:</span> {{ detail.matchedCustomerItemNumber || '-' }}</div>
                            </div>
                        </td>
                        <td class="compact-cell">
                            <div class="stacked-cell">
                                <div><span class="cell-label">Sellout:</span> {{ detail.selloutStartDate | date }} to {{ detail.selloutEndDate | date }}</div>
                                <div><span class="cell-label">Promo:</span> {{ detail.promoStartDate | date }} to {{ detail.promoEndDate | date }}</div>
                                <div><span class="cell-label">Month:</span> {{ detail.monthYear || '-' }}</div>
                            </div>
                        </td>
                        <td class="compact-cell">
                            <div class="stacked-cell">
                                <div><span class="cell-label">Comp/Unit:</span> {{ detail.compensationPerUnit ?? '-' }}</div>
                                <div><span class="cell-label">Approved:</span> {{ detail.approvedCompensationPerUnit ?? '-' }}</div>
                                <div>
                                    <span class="cell-label">Variance:</span>
                                    <span [ngClass]="getVarianceClass(detail.compensationPerUnitVariance)">
                                        {{ formatVariance(detail.compensationPerUnitVariance) }}
                                    </span>
                                </div>
                                <div><span class="cell-label">Qty:</span> {{ detail.quantity ?? '-' }}</div>
                                <div><span class="cell-label">Total:</span> {{ detail.totalAmount ?? '-' }}</div>
                            </div>
                        </td>
                        <td class="validation-cell">
                            <div class="stacked-cell">
                                <div>
                                    <p-tag [value]="getQuantityValidationLabel(detail)"
                                        [severity]="getQuantityValidationSeverity(detail)">
                                    </p-tag>
                                </div>
                                <div><span class="cell-label">Ref Qty:</span> {{ detail.quantityValidationQuantity ?? '-' }}</div>
                                <div class="validation-message">{{ detail.quantityValidationMessage || '-' }}</div>
                            </div>
                        </td>
                        <td class="validation-cell">
                            <div class="stacked-cell">
                                <div>
                                    <p-tag [value]="detail.validationStatus || 'UNKNOWN'"
                                        [severity]="getValidationTagSeverity(detail.validationStatus)">
                                    </p-tag>
                                </div>
                                <div class="validation-message">{{ detail.validationMessage || '-' }}</div>
                            </div>
                        </td>
                    </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                    <tr>
                        <td colspan="6" class="text-center">No sellout details found.</td>
                    </tr>
                </ng-template>
            </p-table>
        </p-fieldset>
    </div>

    <p-divider></p-divider>

    <p-tabs value="0">
        <p-tablist>
            <p-tab value="0">Approvals</p-tab>
            <p-tab value="1">Attachments</p-tab>
        </p-tablist>
        <p-tabpanels>
            <p-tabpanel value="0">
                <app-approval-card [invokedFromChat]="false" [businessObjectId]="selloutHeader.selloutId ?? 0"
                    [businessObjectDescription]="'Sellout #' + (selloutHeader.selloutId ?? '')"
                    [workflowApprovals]="selloutHeader.workflowApprovals" [currentUser]="currentUser"
                    [approvalContext]="{ customerId: selloutHeader.customer.id, businessId: selloutHeader.business?.id, businessUnitId: selloutHeader.businessUnit?.id }"
                    (statusChange)="onStatusChange($event)">
                </app-approval-card>
            </p-tabpanel>
            <p-tabpanel value="1">
                <div class="p-4">
                    <div class="mb-4">
                        <p-fileUpload name="attachments[]" (onSelect)="onFileSelect($event)" [multiple]="true"
                            accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.eml" [maxFileSize]="30000000"
                            [disabled]="!canUploadAttachment" [showUploadButton]="false" [showCancelButton]="false"
                            chooseLabel="Add Attachment" chooseIcon="pi pi-plus">
                        </p-fileUpload>
                    </div>

                    <p-table [value]="selloutHeader.attachments" styleClass="p-datatable-sm" responsiveLayout="scroll"
                        *ngIf="selloutHeader.attachments && selloutHeader.attachments.length > 0">
                        <ng-template pTemplate="header">
                            <tr>
                                <th>File Name</th>
                                <th>Size</th>
                                <th>Uploaded By</th>
                                <th>Uploaded Date</th>
                                <th>Actions</th>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="body" let-attachment>
                            <tr>
                                <td>{{ attachment.fileName }}</td>
                                <td>{{ formatFileSize(attachment.fileSize) }}</td>
                                <td>{{ attachment.uploadedBy || 'N/A' }}</td>
                                <td>{{ attachment.uploadedDate | appDate:'medium' }}</td>
                                <td>
                                    <div class="flex gap-2">
                                        <button pButton pRipple icon="pi pi-eye" class="p-button-rounded p-button-info"
                                            (click)="viewAttachment(attachment)"></button>
                                        <button pButton pRipple icon="pi pi-download"
                                            class="p-button-rounded p-button-success"
                                            (click)="downloadAttachment(attachment)"></button>
                                        <button pButton pRipple icon="pi pi-envelope"
                                            class="p-button-rounded p-button-help"
                                            (click)="emailAttachment(attachment)"></button>
                                    </div>
                                </td>
                            </tr>
                        </ng-template>
                    </p-table>

                    <div *ngIf="attachmentsChanged" class="flex justify-content-end mt-4">
                        <p-button label="Save Attachment Changes" icon="pi pi-save"
                            (click)="saveAttachments()"></p-button>
                    </div>
                </div>
            </p-tabpanel>
        </p-tabpanels>
    </p-tabs>
</div>

<div *ngIf="!isLoading && !selloutHeader" class="p-4 text-center">
    <p>Could not load sellout data. Please check the ID and try again.</p>
</div>

<p-dialog header="Apply Variance Override" [(visible)]="overrideDialogVisible" [modal]="true" [style]="{width: '40rem'}">
    <div class="flex flex-column gap-3">
        <p class="m-0 text-700">
            This override is available only when all details are mapped and none are `NOT_MATCHED` or `MAPPING_ISSUE`.
        </p>
        <textarea pInputTextarea [(ngModel)]="overrideJustification" rows="5" class="w-full"
            placeholder="Enter override justification"></textarea>
        <div class="flex justify-content-end gap-2">
            <p-button label="Cancel" severity="secondary" (onClick)="overrideDialogVisible = false"></p-button>
            <p-button label="Apply Override" icon="pi pi-check" severity="warn"
                [loading]="isApplyingOverride" (onClick)="applyHeaderOverride()"></p-button>
        </div>
    </div>
</p-dialog>

<p-dialog header="View Attachment" [(visible)]="pdfViewerVisible" [modal]="true"
    [style]="{width: '80vw', height: '90vh'}" [maximizable]="true">
    <ng-template pTemplate="content">
        <ngx-extended-pdf-viewer [src]="pdfSrc!" height="85vh"></ngx-extended-pdf-viewer>
    </ng-template>
</p-dialog>
